import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import FailedIcon from 'src/assets/icons/FailedIcon';
import Button from 'src/components/design-system/Button';
import useBoolean from 'src/hooks/useBoolean';
import useCompany from 'src/hooks/useCompany';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useRunOcrScanApi from 'src/services/useRunOcrScanApi';
import DeleteConfirmationModal from './DeleteConfirmationModal';

const headerWebhookError =
  'Unable to scan this file. Please click on Rerun OCR to try again. If it continues to fail, then click Abandon Scan and upload a new file to try again (Error: Error while processing the header webhook)';
const itemWebhookError =
  'Unable to process this file. Please click on Rerun OCR to try again. If it continues to fail, then click Abandon Scan and upload a new file to try again (Error: Error while processing the item webhook)';
const invalidFileError =
  'Unable to read this scan. Please click on Abandon Scan to upload a new file and try again or manually create the document (Error: Invalid file content type)';
const uploadingError =
  'Unable to upload this file. Please delete and re-upload to try again (Error: File not exists in input blob container for DIP processing)';
const defaultError = '';

const errorMessages = {
  0: headerWebhookError,
  1: itemWebhookError,
  2: invalidFileError,
  3: uploadingError,
  4: defaultError,
};

const CanRerunOcr = [0, 1];

const FailedStatusPreview = () => {
  const { previewDocument, refreshPage, ocrFailedValue, enableRefresh, disableRefresh } =
    useInvoiceScanManager();
  const { company: selectedCompany } = useCompany();
  const { ocrScanRun, isOcrScanRunning } = useRunOcrScanApi();
  const { value: isModalOpen, setTrue: openModal, setFalse: closeModal } = useBoolean();
  const [attachmentId, setAttachmentId] = useState<0[]>([]);

  const { runOcrStatus, canDeleteAndMove } = useMemo(() => {
    let runOcrStatus: 0 | 1 | 2 | 3 | 4 = 4;
    let canDeleteAndMove = true;
    if (ocrFailedValue === 'Error while processing the header webhook') {
      runOcrStatus = 0;
    } else if (ocrFailedValue === 'Error while processing the Item webhook') {
      runOcrStatus = 1;
    } else if (ocrFailedValue === 'Invalid file content type') {
      canDeleteAndMove = false;
      runOcrStatus = 2;
    } else if (ocrFailedValue === 'File not exists in input blob container for DIP processing') {
      canDeleteAndMove = false;
      runOcrStatus = 3;
    }

    return {
      runOcrStatus,
      canDeleteAndMove,
    };
  }, [ocrFailedValue]);

  const handleReRunOcr = useCallback(async () => {
    disableRefresh();
    await ocrScanRun({
      companyId: Number(selectedCompany.companyId),
      attachmentIds: attachmentId,
    }).finally(() => enableRefresh());
    toast.success('Rerun OCR Scan has been Successfully completed.');
    refreshPage();
  }, [
    attachmentId,
    disableRefresh,
    enableRefresh,
    ocrScanRun,
    refreshPage,
    selectedCompany.companyId,
  ]);

  const handleAbandonScan = useCallback(() => {
    disableRefresh();
    setAttachmentId(previewDocument?.attachId ? [previewDocument.attachId] : []);
    openModal();
  }, [openModal, previewDocument?.attachId, disableRefresh]);

  return (
    <div className="flex flex-col gap-1 items-center mb-1">
      <div className="flex flex-row gap-[6px] p-[6px] rounded-3xl bg-[#D9534F33] items-center justify-center">
        <FailedIcon />
        <span className="text-[12px] font-medium">Error with scan</span>
      </div>
      <div className="text-center text-[14px] px-2">{errorMessages[runOcrStatus]}</div>
      <div className="flex flex-row justify-center flex-wrap">
        <DeleteConfirmationModal
          attachmentIds={attachmentId}
          canDeleteAndMove={canDeleteAndMove}
          modalState="failed"
          onClose={closeModal}
          visible={isModalOpen}
        />
        <Button color="error" onClick={handleAbandonScan} disabled={isOcrScanRunning}>
          Abandon Scan
        </Button>
        {CanRerunOcr.includes(runOcrStatus) && (
          <Button color="info" onClick={handleReRunOcr} loading={isOcrScanRunning}>
            Rerun OCR
          </Button>
        )}
      </div>
    </div>
  );
};

export default FailedStatusPreview;
